<template>
    <transition name="fade">
        <div class="modal is-active" v-show="visible" id="popup-stock-reconciliation">
            <div class="modal-background" @click="closeModal"></div>
            <div class="modal-content">
                <div class="popup-dialog">
                    <div class="popup-title">
                        <h1 class="title is-1">{{ $t('stock_reconciliation.detail_title') }}</h1>

                        <div class="popup-close" @click.prevent="closeModal">
                            <i class="icon">close</i>
                        </div>
                    </div>

                    <div class="popup-body">
                        <div class="giftcards-table mt-3">
                            <g-table
                                ref="reconciliationsTable"
                                sort-column="id"
                                :url="giftcardsUrl"
                                :per-page="50"
                            >
                                <b-table-column field="code_masked" :label="$t('stock_reconciliation.giftcard')" v-slot="props">
                                    <b>
                                        ...<a
                                            href="#"
                                            class="find-giftcard"
                                            :title="$t('stock_reconciliation.giftcard')"
                                            :aria-label="`${$t('stock_reconciliation.giftcard')} ${props.row.code_masked}`"
                                            @click.prevent="navigateToGiftcard(props.row.id)"
                                        >{{ props.row.code_masked }}</a>
                                    </b>
                                </b-table-column>

                                <b-table-column field="status" :label="$t('stock_reconciliation.status')" v-slot="props">
                                    <span class="tag is-light">
                                        {{ getStatusLabel(props.row.status) }}
                                    </span>
                                    <template v-if="props.row.restored_at">
                                        <br>
                                        <small>
                                            {{ $i18nDate(new Date(props.row.restored_at), 'numeric') }}
                                            <template v-if="props.row.restored_by">
                                                {{ $t('stock_reconciliation.restored_by', { name: props.row.restored_by.first_name + ' ' + props.row.restored_by.last_name }) }}
                                            </template>
                                        </small>
                                    </template>
                                </b-table-column>

                                <b-table-column numeric field="actions" :label="$t('stock_reconciliation.action')" v-slot="props">
                                    <div class="buttons is-right">
                                        <button
                                            v-if="props.row.status === 'restored'"
                                            class="button is-small"
                                            :title="$t('stock_reconciliation.lost')"
                                            :aria-label="$t('stock_reconciliation.lost')"
                                            :class="{'is-loading': isReconciling}"
                                            @click="reconcile(props.row.id)"
                                        >
                                            <span>{{ $t('stock_reconciliation.lost') }}</span>
                                        </button>

                                        <button
                                            v-if="props.row.status === 'reconciled'"
                                            class="button is-small"
                                            :title="$t('stock_reconciliation.present')"
                                            :aria-label="$t('stock_reconciliation.present')"
                                            :class="{'is-loading': isRestoring}"
                                            @click="restore(props.row.id)"
                                        >
                                            <span>{{ $t('stock_reconciliation.present') }}</span>
                                        </button>
                                    </div>
                                </b-table-column>
                            </g-table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
export default {
    props: {
        reconciliationId: {
            type: String,
            required: true,
        },
        companyId: {
            type: [String, Number],
            required: true,
        },
        onClose: {
            type: Function,
            default: null,
        },
    },

    data() {
        return {
            isReconciling: false,
            isRestoring: false,
            hasChanges: false,
        }
    },

    computed: {
        giftcardsUrl() {
            return `/api/admin/company/${this.companyId}/stock-reconciliation/${this.reconciliationId}/giftcards`
        },
    },

    methods: {
        async reconcile(giftcardId) {
            if (this.isReconciling) return
            this.isReconciling = true

            try {
                await axios.patch(
                    `/api/admin/company/${this.companyId}/stock-reconciliation/${this.reconciliationId}`,
                    {
                        giftcards_to_reconcile: [giftcardId],
                        giftcards_to_restore: [],
                    }
                )

                this.hasChanges = true
                this.$refs.reconciliationsTable.reloadCurrentPage()
            } catch (error) {
                this.$buefy.toast.open({ message: this.getRequestErrorMessage(error), type: 'is-danger' })
            } finally {
                this.isReconciling = false
            }
        },

        async restore(giftcardId) {
            if (this.isRestoring) return

            this.isRestoring = true

            try {
                await axios.patch(
                    `/api/admin/company/${this.companyId}/stock-reconciliation/${this.reconciliationId}`,
                    {
                        giftcards_to_reconcile: [],
                        giftcards_to_restore: [giftcardId],
                    }
                )

                this.hasChanges = true
                this.$refs.reconciliationsTable.reloadCurrentPage()
            } catch (error) {
                this.$buefy.toast.open({ message: this.getRequestErrorMessage(error), type: 'is-danger' })
            } finally {
                this.isRestoring = false
            }
        },

        closeModal() {
            if (this.hasChanges === true && this.onClose) {
                this.onClose()
            }

            this.destroy()
        },

        getRequestErrorMessage(error) {
            const errorData = error?.response?.data

            if (errorData?.errors?.length > 0 && errorData.errors[0].detail) {
                return errorData.errors[0].detail
            }

            return errorData?.message || this.$t('stock_reconciliation.error')
        },

        getStatusLabel(status) {
            const labels = {
                'reconciled': this.$t('stock_reconciliation.status_reconciled'),
                'restored': this.$t('stock_reconciliation.status_restored'),
            }

            return labels[status] || status
        },

        navigateToGiftcard(giftcardId) {
            const route = this.$router.resolve({
                name: 'admin.giftcards',
                query: { id: giftcardId }
            })

            window.open(route.href, '_blank')

            this.closeModal()
        },
    }
}
</script>

<style scoped>
.giftcards-table code {
    background-color: #f5f5f5;
    padding: 0.125rem 0.375rem;
    border-radius: 0.25rem;
}

a.find-giftcard {
    color: #1F1F1F;
    letter-spacing: .1rem;
    font-family: 'Courier New', Courier, monospace;
    text-decoration: underline;
}
</style>
